import { z } from 'zod';

import { IMediaReferenceSchema } from './media/media';
import { isPhoneNumber } from './validator/phoneNumber';

export const stringBooleanSchema = z.union([z.boolean(), z.literal('true'), z.literal('false')]).transform((val) => {
  if (typeof val === 'boolean') {
    return val;
  }

  return String(val) === 'true';
});

export const sortOptions = ['ASC', 'DESC'] as const;
export enum SortOrder {
  ASC = 'ASC',
  DESC = 'DESC',
}
export type SortOption = keyof typeof SortOrder;
export const isSortOption = (value: string | undefined): value is SortOption =>
  value !== undefined && sortOptions.includes(value as SortOption);

export const PaginationMetaSchema = z.object({
  totalItems: z.number().optional(),
  itemCount: z.number(),
  itemsPerPage: z.number(),
  totalPages: z.number().nullish(),
  currentPage: z.number(),
});

export const PaginationLinkSchema = z
  .object({
    first: z.string(),
    previous: z.string(),
    next: z.string(),
    last: z.string(),
  })
  .partial()
  .optional();

export const paginateResponseSchema = <T extends z.ZodType>(schema: T) =>
  z.object({
    items: z.array(schema),
    meta: PaginationMetaSchema,
    links: PaginationLinkSchema,
  });

export const CURSOR_PAGINATION_DEFAULT_LIMIT = 25;
export const CURSOR_PAGINATION_MAX_LIMIT = 100;

export const cursorPaginateResponseSchema = <T extends z.ZodType>(schema: T) =>
  z.object({
    items: z.array(schema),
    hasMore: z.boolean(),
    nextCursor: z.string().nullable(),
  });

export const cursorPaginateQuerySchema = z.object({
  after: z.string().optional(),
  limit: z.coerce.number().min(1).max(CURSOR_PAGINATION_MAX_LIMIT).default(CURSOR_PAGINATION_DEFAULT_LIMIT),
});

export const ComparableFilterSchema = z.object({
  operator: z.enum(['equals', 'greater_than', 'less_than']),
  name: z.string(),
  value: z.string(),
});
export const InFilterSchema = z.object({
  operator: z.literal('in'),
  name: z.string(),
  value: z.array(z.string()),
});
export const RangeFilterSchema = z.object({
  operator: z.literal('range'),
  name: z.string(),
  value: z.object({ from: z.string(), to: z.string() }),
});
export const FiltersSchema = z.array(z.union([ComparableFilterSchema, InFilterSchema, RangeFilterSchema]));

export type FiltersType = z.infer<typeof FiltersSchema>;

export const paginateQuerySchema = <SORT_BY_FIELDS extends readonly [string, ...string[]]>({
  sortBy,
}: {
  sortBy?: SORT_BY_FIELDS;
} = {}) => {
  return z.object({
    page: z.coerce.number().min(1).default(1),
    limit: z.coerce.number().min(0).default(10),
    sortDir: z.enum(sortOptions).optional(),
    sortBy: sortBy === undefined ? z.never().optional() : z.enum(sortBy).optional(),
    filters: FiltersSchema.optional(),
    q: z.string().optional(),
  });
};

const LiteralSchema = z.union([z.string(), z.number(), z.boolean(), z.null()]);
type Literal = z.infer<typeof LiteralSchema>;
export type Json = Literal | { [key: string]: Json } | Json[];
export const JsonSchema: z.ZodType<Json> = z.lazy(() => z.union([LiteralSchema, z.array(JsonSchema), z.record(JsonSchema)]));

export type IMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export const makeEndpoint = <
  QUERY_SCHEMA extends z.ZodType | undefined,
  URL_PARAMS extends QUERY_SCHEMA extends undefined
    ? Record<string, unknown> | undefined
    : {
        [key: string]: unknown;
        query: QUERY_SCHEMA extends undefined ? undefined : z.input<Exclude<QUERY_SCHEMA, undefined>>;
      },
  REQUEST_SCHEMA extends z.ZodType,
  RESPONSE_SCHEMA extends z.ZodType,
  METHOD extends IMethod,
>(
  endpoint: Readonly<{
    url: (params: URL_PARAMS) => string;
    method: METHOD;
    requestSchema: REQUEST_SCHEMA;
    responseSchema: RESPONSE_SCHEMA;
    querySchema?: QUERY_SCHEMA;
    title?: string;
    description?: string;
  }>,
): (typeof endpoint)['querySchema'] extends undefined
  ? Omit<typeof endpoint, 'querySchema'>
  : Omit<typeof endpoint, 'querySchema'> & { querySchema: NonNullable<(typeof endpoint)['querySchema']> } =>
  endpoint as (typeof endpoint)['querySchema'] extends undefined
    ? Omit<typeof endpoint, 'querySchema'>
    : Omit<typeof endpoint, 'querySchema'> & { querySchema: NonNullable<(typeof endpoint)['querySchema']> };

export const PhoneNumberValidationSchema = z
  .string()
  .refine((phoneNumber) => isPhoneNumber(phoneNumber, { validateOnlyCountry: true }), {
    message: 'Invalid phone number, please verify proper format.',
  });

export enum ENTITY {
  ORG = 'organization',
  BRAND = 'brand',
}

export enum REST_API_ENTITY {
  ORG = 'organizations',
  BRAND = 'brands',
}

export interface IEntityTypeContext {
  entityType: ENTITY;
  entityId?: string;
}

export interface IEntityContext {
  entityType: ENTITY;
  entityId: string;
}

export interface IRestApiEntityTypeContext {
  entityType: REST_API_ENTITY;
  entityId?: string;
}

export interface IRestApiEntityContext {
  entityType: REST_API_ENTITY;
  entityId: string;
}

export type IEndpointSchema<T extends { responseSchema: z.ZodType; requestSchema: z.ZodType }> = {
  response: z.infer<T['responseSchema']>;
  request: z.infer<T['requestSchema']>;
};

export type IRestEndpointSchema<T extends { responseSchema: z.ZodType; requestSchema: z.ZodType }> = {
  response: { data: z.infer<T['responseSchema']> };
  request: z.infer<T['requestSchema']>;
};

export const MinimalHostSchema = z.object({
  id: z.string(),
  fullName: z.string(),
  profilePicture: IMediaReferenceSchema.nullable(),
});

export const HostSchema = MinimalHostSchema.extend({
  firstName: z.string(),
  lastName: z.string(),
  allowOverlappingBookings: z.boolean(),
});

export interface PaginatedResponse<T> {
  items: T[];
  meta: z.infer<typeof PaginationMetaSchema>;
  links: z.infer<typeof PaginationLinkSchema>;
}

export interface CursorPaginatedResponse<T> {
  items: T[];
  hasMore: boolean;
  nextCursor: string | null;
}

export enum Env {
  PRODUCTION = 'production',
  STAGING = 'staging',
  PREVIEW = 'preview',
  TEST = 'test',
  DEVELOPMENT = 'development',
}

export enum TruthyString {
  TRUE = 'true',
  ONE = '1',
}

export const isHigherEnv = (env: string) => {
  return [Env.STAGING, Env.PRODUCTION].includes(env as Env);
};

export const isLowerEnv = (env: string) => {
  return !isHigherEnv(env as Env);
};
